import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/react';
import { DRXError, toDRXError } from './drx-error';
import { isStaleAccountError } from './stale-account';

const DEFAULT_SENTRY_DSN =
  'https://eb245777eccb0e85bbd8554ff2900054@o4511175110098944.ingest.us.sentry.io/4511175117045760';

const getTracesSampleRate = (): number => {
  const rawValue = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return import.meta.env.DEV ? 1.0 : 0.2;
  }

  const parsed = Number(rawValue);
  if (Number.isFinite(parsed) && parsed >= 0 && parsed <= 1) {
    return parsed;
  }

  return import.meta.env.DEV ? 1.0 : 0.2;
};

let initialized = false;

const dedupeByMessage = new Map<string, number>();
const DEDUPE_WINDOW_MS = 4000;

const mapErrorLevelToSentry = (level: DRXError['level']): SeverityLevel => {
  switch (level) {
    case 'fatal':
      return 'fatal';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'error':
    default:
      return 'error';
  }
};

const normalizeMessage = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message || value.name || 'Unknown error';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const shouldSkipDuplicate = (error: DRXError, context?: string): boolean => {
  const key = `${context || 'unknown'}:${error.code}:${error.devMessage}`;
  const now = Date.now();
  const lastSeen = dedupeByMessage.get(key);

  dedupeByMessage.set(key, now);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
    return true;
  }

  return false;
};

// Requests still in flight when the page is left (a reload, a typed URL, a
// test runner moving on) fail on the client -- Firebase callables report it as
// "internal" -- although the server answered fine. Nobody saw an error.
let pageUnloading = false;
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => { pageUnloading = true; });
  window.addEventListener("beforeunload", () => { pageUnloading = true; });
  window.addEventListener("pageshow", () => { pageUnloading = false; });
}

export const initSentry = (): void => {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN;
  if (!dsn) return;

  const sentryEnvironment =
    typeof import.meta.env.VITE_SENTRY_ENVIRONMENT === "string" &&
    import.meta.env.VITE_SENTRY_ENVIRONMENT.trim() !== ""
      ? import.meta.env.VITE_SENTRY_ENVIRONMENT.trim()
      : import.meta.env.MODE;

  const sentryRelease =
    typeof import.meta.env.VITE_SENTRY_RELEASE === "string" &&
    import.meta.env.VITE_SENTRY_RELEASE.trim() !== ""
      ? import.meta.env.VITE_SENTRY_RELEASE.trim()
      : typeof __APP_VERSION__ !== "undefined"
        ? `dancerealmx@${__APP_VERSION__}`
        : undefined;

  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: sentryEnvironment,
    ...(sentryRelease ? { release: sentryRelease } : {}),
    tracesSampleRate: getTracesSampleRate(),
    ignoreErrors: [
      // Stale-chunk errors after a deploy. main.tsx already recovers from
      // these by reloading once to pick up the fresh index.html, so the user
      // never sees a broken page -- but Sentry still recorded every one. With
      // frequent releases that buries real errors under noise, which is the
      // opposite of what an error tracker is for.
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
      /Loading chunk [\w-]+ failed/i,
      /not a valid JavaScript MIME type/i,
      /must be used within a.*Provider/i,
      /FirebaseAuthProvider/i,
      /chrome-extension:/i,
      /Identifier.*already been declared/i,
      /pageSaving\.js/i,
      /messagingInject\.js/i,
      // The visitor's connection, not our code: Firestore offline, analytics'
      // installation call blocked or dropped, Safari clearing site storage
      // under Firebase. Firestore retries when the connection comes back.
      /client is offline/i,
      /firebaseinstallations\.googleapis\.com/i,
      /Database deleted by request of the user/i,
    ],
    beforeSend(event, hint) {
      if (pageUnloading) return null;
      // Refused because the account had just changed (see stale-account.ts).
      if (isStaleAccountError(hint.originalException)) return null;
      const original = hint.originalException;
      const normalizedMessage = normalizeMessage(original || event.message || event.exception?.values?.[0]?.value);

      event.tags = {
        ...event.tags,
        client: 'web',
        normalized: 'true',
      };

      event.extra = {
        ...event.extra,
        normalizedMessage,
        pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
        href: typeof window !== 'undefined' ? window.location.href : undefined,
      };

      if (!event.fingerprint || event.fingerprint.length === 0) {
        const exceptionType = event.exception?.values?.[0]?.type || 'UnknownError';
        event.fingerprint = ['{{ default }}', exceptionType, normalizedMessage];
      }

      return event;
    },
  });

  initialized = true;
};

export const captureErrorToSentry = (
  error: unknown,
  options: {
    context?: string;
    extra?: Record<string, unknown>;
    tags?: Record<string, string>;
  } = {}
): DRXError => {
  const drxError = toDRXError(error);

  if (isStaleAccountError(error) || isStaleAccountError(drxError)) return drxError;
  if (!initialized || shouldSkipDuplicate(drxError, options.context)) {
    return drxError;
  }

  Sentry.withScope((scope) => {
    scope.setLevel(mapErrorLevelToSentry(drxError.level));
    scope.setTag('drx.code', drxError.code);

    if (options.context) {
      scope.setTag('drx.context', options.context);
    }

    Object.entries(drxError.tags || {}).forEach(([key, value]) => {
      scope.setTag(key, value);
    });

    Object.entries(options.tags || {}).forEach(([key, value]) => {
      scope.setTag(key, value);
    });

    if (drxError.fingerprint && drxError.fingerprint.length > 0) {
      scope.setFingerprint(drxError.fingerprint);
    }

    const mergedExtra = {
      ...drxError.context,
      ...options.extra,
      userMessage: drxError.userMessage,
      devMessage: drxError.devMessage,
    };

    Object.entries(mergedExtra).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    const cause = (drxError as Error & { cause?: unknown }).cause;
    if (cause instanceof Error) {
      Sentry.captureException(cause);
      return;
    }

    Sentry.captureException(drxError);
  });

  return drxError;
};

let lastSentryAuthRoles: string[] = [];
let lastSentryAuthWasLoggedIn = false;

export const setSentryUserContext = (user: {
  id?: string | null;
  email?: string | null;
  username?: string | null;
  subscriptionTier?: string | null;
  roles?: string[] | null;
} | null): void => {
  if (!initialized) return;

  if (!user?.id) {
    lastSentryAuthRoles = [];
    lastSentryAuthWasLoggedIn = false;
    Sentry.setUser(null);
    Sentry.setTag('authState', 'anonymous');
    Sentry.setTag('subscriptionTier', 'none');
    Sentry.setContext('auth', { authState: 'anonymous' });
    return;
  }

  lastSentryAuthWasLoggedIn = true;
  lastSentryAuthRoles = user.roles || [];

  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
    username: user.username || undefined,
  });

  Sentry.setTag('authState', 'authenticated');
  Sentry.setTag('subscriptionTier', user.subscriptionTier || 'none');
  Sentry.setContext('auth', {
    roles: lastSentryAuthRoles,
    subscriptionTier: user.subscriptionTier || 'none',
  });
};

export type SentrySubscriptionSnapshot = {
  /** When absent, subscription tags stay neutral (do not report `free` for logged-out users). */
  userId?: string | null;
  tier: string | null | undefined;
  status?: string | null;
  source?: string | null;
  loading?: boolean;
  hasPaidAccess?: boolean;
  isSubscriptionConsistent?: boolean;
};

/**
 * Push canonical subscription state (`subscriptions/{uid}` via `useSubscription`) into Sentry.
 * Call from `SentryContextSync` only; requires `userId` so anonymous sessions are not tagged as `free`.
 */
export const syncSentrySubscriptionFromApp = (snapshot: SentrySubscriptionSnapshot): void => {
  if (!initialized) return;

  if (!snapshot.userId) {
    Sentry.setTag("subscriptionTier", "none");
    Sentry.setTag("subscriptionStatus", "none");
    Sentry.setTag("subscriptionSource", "none");
    Sentry.setTag("subscriptionLoading", "false");
    Sentry.setTag("hasPaidSubscription", "false");
    Sentry.setTag("subscriptionConsistent", "unknown");
    Sentry.setContext("subscription", {
      tier: "none",
      status: "none",
      source: "none",
      loading: false,
      hasPaidAccess: false,
      note: "logged_out_or_no_uid",
    });
    return;
  }

  const tierTag =
    snapshot.tier != null && String(snapshot.tier).trim() !== ""
      ? String(snapshot.tier).toLowerCase()
      : "none";

  Sentry.setTag("subscriptionTier", tierTag);
  Sentry.setTag(
    "subscriptionStatus",
    snapshot.status != null && String(snapshot.status).trim() !== ""
      ? String(snapshot.status).toLowerCase()
      : "unknown"
  );
  Sentry.setTag(
    "subscriptionSource",
    snapshot.source != null && String(snapshot.source).trim() !== ""
      ? String(snapshot.source)
      : "unknown"
  );
  Sentry.setTag("subscriptionLoading", snapshot.loading ? "true" : "false");
  Sentry.setTag("hasPaidSubscription", snapshot.hasPaidAccess ? "true" : "false");
  Sentry.setTag(
    "subscriptionConsistent",
    snapshot.isSubscriptionConsistent === undefined
      ? "unknown"
      : snapshot.isSubscriptionConsistent
        ? "true"
        : "false"
  );

  if (!lastSentryAuthWasLoggedIn) return;

  Sentry.setContext("auth", {
    roles: lastSentryAuthRoles,
    subscriptionTier: tierTag,
  });

  Sentry.setContext("subscription", {
    tier: tierTag,
    status: snapshot.status ?? null,
    source: snapshot.source ?? null,
    loading: snapshot.loading ?? false,
    hasPaidAccess: snapshot.hasPaidAccess ?? false,
    isConsistent: snapshot.isSubscriptionConsistent,
  });
};
